import React, { useCallback, useEffect, useState, useRef } from 'react';
import { Dimensions, StyleSheet, View } from 'react-native';
import { useAppSelector, useAppDispatch } from '../hooks/hooks';

import { Map } from '@components/Map';
import { getMarkers } from '@data/mapData';
import { getRoutes } from '@data/routeData';
import { watch as watchLoc, stopWatching } from '@utils/location';
import { DonationButton } from 'components/map-buttons/Donation';
import { SOSButton } from 'components/map-buttons/SOS';
import { MarkerPopup } from 'components/menus/MarkerPopup';
import {
  Coordinate,
  LocationType,
  PathType,
  RouteSegment,
  RouteType,
} from 'types/map';
import { MarkerType } from 'types/marker';
import { Notification } from '@components/Notification';
import {
  watchNotifications,
  saveSeen as saveSeenNotifications,
} from 'data/notificationData';
import {
  getCurrentDestinations,
  getPathData,
  saveCurrentDestinations,
} from 'data/navigation';

import { ReportButton } from 'components/map-buttons/ReportButton';
import { ReportPopup } from 'components/menus/ReportPopup';
import { HelpPopup } from '@components/HelpPopup';
import { ErrorNotification } from 'components/reusable/ErrorNotification';
import { LayerToggle } from 'components/map-buttons/LayerToggle';
import { RouteDetailsPopup } from 'components/menus/RouteDetailsPopup';
import { RouteButton } from 'components/map-buttons/RouteButton';
import { RoutesPopup } from 'components/menus/RoutesPopup';
import {
  ERROR,
  GEOLOCATION_ERROR,
  LOCATION_PERMISSION_ERROR,
} from 'data/strings';
// import { getColors } from 'utils/pathing';
import Popup from 'components/reusable/Popup';
import { Tutorial } from 'components/Tutorial';
import { getTutorialState, saveTutorialState } from 'data/tutorial';
import { DimensionsContext } from 'context/DimensionsContext';
import { getAreas } from 'data/areas';
import {
  replace as replaceNotifications,
  remove as removeNotification,
} from 'hooks/notificationsSlice';
import { replace as replaceMarkers } from 'hooks/markersSlice';
import { replace as replaceRoutes } from 'hooks/routesSlice';
import { replace as replaceAreas } from 'hooks/areasSlice';
import { SheetManager, SheetProvider } from 'react-native-actions-sheet';
import { AreaPopup } from 'components/menus/AreaPopup';

const entranceMarker = {
  id: 'ENTRANCE',
  title: 'Entrance',
  latlng: [40.83436990805725, -72.61520326137544] as Coordinate,
};

export function Mapview() {
  const dispatch = useAppDispatch();
  const notifications = useAppSelector((state) => state.notifications);
  const markers = useAppSelector((state) => state.markers);
  const routes = useAppSelector((state) => state.routes);
  const areas = useAppSelector((state) => state.areas);

  const [currentNotification, setCurrentNotification] = useState(null);
  const [location, setLocation] = useState<LocationType[]>([]);
  const [markerDetails, setMarkerDetails] = useState(null);
  const [areaDetails, setAreaDetails] = useState(null);
  const [path, setPath] = useState<PathType>(null);
  const [reportInitiated, setReportInitiated] = useState(false);
  const [helpInitiated, setHelpInitiated] = useState(false);
  const [error, setError] = useState(null);
  const [withinBounds, setWithinBounds] = useState(false);
  const [isSatelliteView, setIsSatelliteView] = useState(false);
  const [routeDetails, setRouteDetails] = useState(null);
  const [viewRoutes, setViewRoutes] = useState(false);
  const [destinations, setDestinations] = useState<MarkerType[]>(null);
  const [tutorialSeen, setTutorialSeen] = useState(true);
  const [tutorialMode, setTutorialMode] = useState(false);
  const sosRef = useRef(null);
  const reportRef = useRef(null);
  const donationRef = useRef(null);
  const mapRef = useRef(null);
  const terrainRef = useRef(null);
  const window = Dimensions.get('window');
  const [dimensions, setDimensions] = useState({
    ...window,
    size: window.width >= 900 ? 'large' : 'small',
  });
  const [tutorialItems] = useState([
    {
      title: 'Help',
      body: 'In the help menu, you can request navigation help if you get lost.',
      ref: sosRef,
    },
    {
      title: 'Report',
      body: 'Use this menu to report anything that the park needs to know or address.',
      ref: reportRef,
    },
    {
      title: 'Donation',
      body: 'This button will take you to the donation page.',
      ref: donationRef,
    },
    {
      title: 'Terrain toggle',
      body: 'This lets you switch between a more detailed terrain view and the simplified view.',
      ref: terrainRef,
    },
    {
      title: 'Using the map',
      body: 'You can click on the paths and attraction markers to navigation and view more information.',
      ref: mapRef,
    },
  ]);
  const [isLoaded, setIsLoaded] = useState(false);

  const notificationWatcher = useCallback(
    (data) => {
      if (data && data[0]) {
        dispatch(replaceNotifications({ notifications: data }));
        setCurrentNotification(data[0]);
      }
    },
    [dispatch, setCurrentNotification],
  );

  const { height } = Dimensions.get('window');
  const styles = getStyles(height);

  function dismissAllPopups() {
    setMarkerDetails(null);
    setReportInitiated(false);
    setHelpInitiated(false);
    setRouteDetails(false);
    setViewRoutes(false);
    SheetManager.hideAll();
  }

  const navigate = useCallback(
    async (destinationMarkers: MarkerType[]) => {
      if (location && location[0]) {
        if (withinBounds) {
          const paths = destinationMarkers.map((marker) => marker.latlng);

          const pathData = await getPathData({
            routes: [[location[0].lat, location[0].lng], ...paths],
            avoid: areas.value.filter((area) => area.block === true),
          });

          if (pathData) {
            pathData.segments = pathData.segments.map((segment, index) => {
              segment.name = destinationMarkers[index].title;
              return segment;
            });

            // const withColors = getColors(pathData, routes.value);
            return setPath({ data: pathData, routes: [pathData] });
          } else {
            setDestinations(null);
            setError(ERROR);
            return;
          }
        }

        setDestinations(null);
        setError(GEOLOCATION_ERROR);
      } else {
        setDestinations(null);
        setError(LOCATION_PERMISSION_ERROR);
      }
    },
    [location, withinBounds, areas],
  );

  function setCurrentPath(route: RouteType) {
    if (location && location[0]) {
      if (withinBounds) {
        return setPath({ data: route, routes: [route] });
      }

      setError(GEOLOCATION_ERROR);
    } else {
      setError(LOCATION_PERMISSION_ERROR);
    }
  }

  function navigateExit() {
    setDestinations([entranceMarker]);
  }

  function dismissNotification() {
    saveSeenNotifications([notifications.value[0]]);
    removeNotification({ notification: notifications.value[0] });
    setCurrentNotification(notifications.value[1]);
  }

  useEffect(() => {
    getTutorialState().then((state) => {
      setTutorialSeen(state.seen);
    });

    getMarkers().then((data) => {
      dispatch(replaceMarkers({ markers: data }));

      getRoutes().then((routeData) => {
        dispatch(replaceRoutes({ routes: routeData }));
      });

      watchNotifications(notificationWatcher);

      getAreas().then((areaData) => {
        dispatch(replaceAreas({ areas: areaData }));
        setIsLoaded(true);
      });
    });

    const watchID = watchLoc((loc) => {
      setLocation((l) => [loc, ...l]);
    });

    return () => {
      stopWatching(watchID);
    };
  }, [notificationWatcher, dispatch, setPath]);

  useEffect(() => {
    if (isLoaded) {
      if (destinations?.length > 0) {
        navigate(destinations);
        saveCurrentDestinations(destinations);
      } else if (destinations?.length <= 0) {
        saveCurrentDestinations(null);
        setPath(null);
      } else {
        setPath(null);
      }
    }
  }, [destinations, navigate, isLoaded]);

  useEffect(() => {
    if (withinBounds) {
      getCurrentDestinations().then((des) => {
        if (des) {
          setDestinations(des);
        }
      });
    }
  }, [withinBounds]);

  useEffect(() => {
    const subscription = Dimensions.addEventListener(
      'change',
      ({ window: windowSize }) => {
        setDimensions({
          ...windowSize,
          size: windowSize.width >= 900 ? 'large' : 'small',
        });
      },
    );
    return () => subscription?.remove();
  });

  return (
    <DimensionsContext.Provider value={dimensions}>
      <SheetProvider>
        <View style={styles.container} ref={mapRef}>
          <Map
            markers={markers.value}
            location={location}
            routes={routes.value}
            setMarker={(data) => {
              dismissAllPopups();
              setMarkerDetails(data);
              SheetManager.show('marker');
            }}
            path={path}
            onPress={dismissAllPopups}
            setWithinBounds={setWithinBounds}
            isSatelliteView={isSatelliteView}
            setRouteDetails={(data) => {
              dismissAllPopups();
              setTimeout(() => {
                setRouteDetails(data);
              });
              SheetManager.show('routeDetails');
            }}
            areas={areas.value}
            setArea={(data) => {
              dismissAllPopups();
              setAreaDetails(data);
              SheetManager.show('area');
            }}
          />
          <View style={styles.buttonContainer}>
            <View style={styles.button}>
              <SOSButton
                ref={sosRef}
                onPress={() => {
                  SheetManager.show('sos');
                  dismissAllPopups();
                  setHelpInitiated(!helpInitiated);
                }}
              />
            </View>
            <View style={styles.button}>
              <ReportButton
                ref={reportRef}
                onPress={() => {
                  dismissAllPopups();
                  setReportInitiated(!reportInitiated);
                  SheetManager.show('report');
                }}
              />
            </View>
            <View style={styles.button}>
              <DonationButton ref={donationRef} />
            </View>
            {path && (
              <View style={styles.button}>
                <RouteButton
                  onPress={() => {
                    setViewRoutes(!viewRoutes);
                    SheetManager.show('routesPopup');
                  }}
                />
              </View>
            )}
          </View>
          <View style={styles.buttonContainerLeft}>
            <View style={styles.button}>
              <LayerToggle
                ref={terrainRef}
                onPress={() => setIsSatelliteView(!isSatelliteView)}
                state={isSatelliteView}
              />
            </View>
          </View>
          <MarkerPopup
            marker={markerDetails}
            close={() => setMarkerDetails(null)}
            addDestination={(marker) => {
              SheetManager.hide('marker');
              setDestinations((des) => {
                if (des) {
                  return [...des, marker];
                }

                return [marker];
              });
            }}
            navigate={navigate}
            open={markerDetails}
          />
          <AreaPopup
            area={areaDetails}
            close={() => setAreaDetails(null)}
            open={areaDetails}
          />
          {currentNotification && !tutorialMode && tutorialSeen && (
            <Notification
              data={currentNotification}
              dismiss={dismissNotification}
            />
          )}
          <ReportPopup
            location={location}
            close={() => setReportInitiated(false)}
            open={reportInitiated}
          />
          <HelpPopup
            navigateExit={navigateExit}
            close={() => setHelpInitiated(false)}
            open={helpInitiated}
          />
          <RouteDetailsPopup
            route={routeDetails}
            close={() => setRouteDetails(null)}
            navigate={setCurrentPath}
            open={routeDetails}
          />
          <RoutesPopup
            route={path?.data}
            close={() => setViewRoutes(false)}
            setDestinations={(segments: RouteSegment[]) => {
              if (segments) {
                const newOrder = segments.map((segment) =>
                  destinations.find((des) => des.title === segment.name),
                );
                setDestinations(newOrder);
              } else {
                setDestinations([]);
              }
            }}
            open={viewRoutes}
          />

          {error && (
            <ErrorNotification text={error} close={() => setError(null)} />
          )}

          {!tutorialSeen && (
            <Popup
              title="Welcome to the Quogue Wildlife Refuge navigation map!"
              body="Would you like to view the tutorial of how to use it?"
              buttons={[
                {
                  text: 'View tutorial',
                  action: () => {
                    setTutorialSeen(true);
                    setTutorialMode(true);
                  },
                },
                {
                  text: 'Dismiss',
                  action: () => {
                    setTutorialSeen(true);
                    saveTutorialState();
                  },
                },
              ]}
            />
          )}

          {tutorialMode && (
            <Tutorial
              items={tutorialItems}
              dismiss={() => {
                saveTutorialState();
                setTutorialMode(false);
              }}
            />
          )}
        </View>
      </SheetProvider>
    </DimensionsContext.Provider>
  );
}

function getStyles(height) {
  return StyleSheet.create({
    container: {
      flex: 1,
      width: '100%',
      height,
      overflow: 'hidden',
    },
    buttonContainer: {
      position: 'absolute',
      zIndex: 999,
      right: 0,
      padding: 10,
    },
    buttonContainerLeft: {
      position: 'absolute',
      zIndex: 999,
      left: 0,
      top: 75,
      padding: 10,
    },
    button: {
      marginBottom: 10,
    },
  });
}
